/**
 * B2C Intent & Social Listening — public-source mention monitor.
 * Free sources: Reddit (via Apify fallback) + Hacker News + RSS.
 * Visually distinct from the B2B side (warmer purple/orange palette).
 */
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Radar, MessageCircle, AlertTriangle, Sparkles, Megaphone, Search, Plus,
  Loader2, Globe, Filter, ExternalLink, Hash, Settings, Trash2, Mic2,
  ThumbsUp, ThumbsDown, RefreshCw, Bot, BookOpen, Send, Phone, Bell,
  Flame, Thermometer, Snowflake, X as XIcon, ListPlus, Wand2, Eye,
  Power, Info, Ban, ChevronDown, ChevronUp, ArrowRight,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useLang } from "../i18n/LangContext";
import { useAutoRefresh, AutoRefreshToggle } from "../hooks/useAutoRefresh";
import IntentKeywordsControlPanel from "../components/IntentKeywordsControlPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: "overview",     pt: "Visão Geral",            en: "Overview",           icon: Radar },
  { id: "monitoring",   pt: "Monitoramento",          en: "Monitoring",         icon: MessageCircle },
  { id: "alerts",       pt: "Alertas Web",            en: "Web Alerts",         icon: AlertTriangle },
  { id: "intent-keywords",      pt: "Palavras de Intenção",   en: "Intent Keywords",    icon: BookOpen },
  { id: "opportunities",pt: "Oportunidades",          en: "Opportunities",      icon: Sparkles },
  { id: "sentiment",    pt: "Sentimento",             en: "Sentiment",          icon: Mic2 },
  { id: "replies",      pt: "Respostas IA",           en: "AI Replies",         icon: Bot },
  { id: "sources",      pt: "Fontes",                 en: "Sources",            icon: Globe },
  { id: "settings",     pt: "Configurações",          en: "Settings",           icon: Settings },
];

export default function B2CIntent() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [section, setSection] = useState("overview");
  const [quickKw, setQuickKw] = useState("");

  const runQuickSearch = async (e) => {
    e?.preventDefault?.();
    if (!quickKw.trim()) return;
    // Jump to monitoring tab + persist keyword for SearchPanel to pick up.
    try { window.localStorage.setItem("alp.b2c.quick_kw", quickKw.trim()); } catch (_e) { /* ignore */ }
    setSection("monitoring");
    window.dispatchEvent(new CustomEvent("alp.b2c.quickSearch", { detail: { keywords: quickKw.trim() } }));
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--al-bg)" }} data-testid="b2c-intent-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col">
        <header className="flex items-center gap-3 px-4 lg:px-8 h-[70px] border-b border-[var(--al-border)] bg-[var(--al-card-bg)]">
          <Logo size={28} />
          <h1 className="text-[16px] font-bold tracking-tight">
            <span className="text-[#8B5CF6]">B2C Intent</span> <span className="text-[var(--al-text-muted)]">& Social Listening</span>
          </h1>
          <span className="ml-3 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">
            {isPt ? "Fontes Gratuitas" : "Free Sources"}
          </span>
          {/* Quick-search bar, shaped after SearchBar on Prospecting */}
          <form onSubmit={runQuickSearch} data-testid="b2c-quick-search" className="hidden md:flex items-center gap-1.5 ml-3 flex-1 max-w-md">
            <div className="relative flex-1">
              <input
                type="text"
                value={quickKw}
                onChange={(e) => setQuickKw(e.target.value)}
                data-testid="b2c-quick-search-input"
                placeholder={isPt
                  ? "ex.: procuro CRM, melhor plano de saúde, dor X…"
                  : "e.g.: looking for CRM, best health plan, pain point X…"}
                className="al-input w-full h-9 pl-8 pr-9 text-[13px]"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8B5CF6]">
                <Search className="w-3.5 h-3.5" />
              </span>
              {quickKw && (
                <button
                  type="button"
                  onClick={() => setQuickKw("")}
                  data-testid="b2c-quick-search-clear"
                  title={isPt ? "Limpar" : "Clear"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full text-[var(--al-text-muted)] hover:text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              )}
            </div>
            <button
              type="submit"
              data-testid="b2c-quick-search-submit"
              className="h-9 px-3 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[12px] font-bold inline-flex items-center gap-1 disabled:opacity-60"
            >
              {isPt ? "Buscar" : "Search"}
            </button>
          </form>
          <div className="ml-auto flex items-center gap-2">
            <ClaireHelpButton testid="b2c-intent"
              question="Como uso o B2C Intent & Social Listening — monitorar menções, detectar oportunidades e gerar respostas com IA?" />
            <LangToggle />
            <ThemeToggle />
          </div>
        </header>

        <div className="flex">
          <aside className="w-[230px] shrink-0 border-r border-[var(--al-border)] bg-[var(--al-card-bg)] py-5 px-3 hidden lg:block min-h-[calc(100vh-70px)]">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.id;
              return (
                <button key={s.id} type="button"
                  data-testid={`b2c-nav-${s.id}`}
                  onClick={() => setSection(s.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] mb-0.5 transition-colors ${
                    active
                      ? "bg-[#F3E8FF] text-[#8B5CF6] font-bold"
                      : "text-[var(--al-text-muted)] hover:bg-[var(--al-bg)] hover:text-[var(--al-text)]"
                  }`}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">{isPt ? s.pt : s.en}</span>
                </button>
              );
            })}
          </aside>

          <main className="flex-1 px-6 lg:px-10 py-8 max-w-[1320px]">
            {section === "overview" && <OverviewTab isPt={isPt} goTo={setSection} />}
            {section === "monitoring" && <MentionsTab isPt={isPt} />}
            {section === "alerts" && <AlertsTab isPt={isPt} />}
            {section === "intent-keywords" && (
              <>
                <IntentKeywordsControlPanel isPt={isPt} />
                {/* HOT alerts (Telegram + Twilio) — kept right below the new control panel */}
                <div className="mt-6"><HotAlertsCard isPt={isPt} /></div>
              </>
            )}
            {section === "opportunities" && <OpportunitiesTab isPt={isPt} />}
            {section === "sentiment" && <SentimentTab isPt={isPt} />}
            {section === "replies" && <RepliesTab isPt={isPt} />}
            {section === "sources" && <SourcesTab isPt={isPt} />}
            {section === "settings" && <SettingsTab isPt={isPt} />}
          </main>
        </div>
      </div>
    </div>
  );
}

// ---- Overview tab ----
function OverviewTab({ isPt, goTo }) {
  const [data, setData] = useState({ cards: {}, by_source: [], by_intent: [] });
  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/b2c-intent/dashboard`);
      setData(r.data);
    } catch (_e) { /* ignore */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useAutoRefresh(reload, 30000, { defaultEnabled: true });
  const c = data.cards || {};

  // Iter51 — clickable KPIs. Each card links to the section that lets the
  // user dig deeper, optionally seeding a filter via localStorage so the
  // target tab can pre-apply it.
  const seedFilter = (filter) => {
    try { window.localStorage.setItem("alp.b2c.overview_filter", JSON.stringify(filter || {})); } catch (_e) { /* ignore */ }
  };
  const cards = [
    { label: isPt ? "Hoje" : "Today",                      value: c.today || 0,                tone: "text-[#8B5CF6]",
      target: "monitoring", filter: { since_today: true } },
    { label: isPt ? "Alta intenção" : "High intent",       value: c.high_intent || 0,          tone: "text-emerald-600",
      target: "opportunities", filter: { intent_min: 70 } },
    { label: isPt ? "Reclamações concorrentes" : "Competitor complaints", value: c.competitor_complaints || 0, tone: "text-rose-600",
      target: "monitoring", filter: { intent_category: "complaint_competitor" } },
    { label: isPt ? "Pedidos de recomendação" : "Recommendation requests", value: c.recommendation_requests || 0, tone: "text-amber-600",
      target: "monitoring", filter: { intent_category: "recommendation_request" } },
    { label: isPt ? "Convertidas" : "Converted",           value: c.converted || 0,            tone: "text-blue-600",
      target: "opportunities", filter: { converted: true } },
    { label: isPt ? "Positivas" : "Positive",              value: c.positive || 0,             tone: "text-emerald-700",
      target: "sentiment", filter: { sentiment: "positive" } },
    { label: isPt ? "Negativas" : "Negative",              value: c.negative || 0,             tone: "text-rose-700",
      target: "sentiment", filter: { sentiment: "negative" } },
    { label: isPt ? "Respostas IA" : "AI replies",         value: c.replies_generated || 0,    tone: "text-indigo-600",
      target: "replies", filter: {} },
    { label: isPt ? "Total de menções" : "Total mentions", value: c.total_mentions || 0,       tone: "text-[var(--al-text)]",
      target: "monitoring", filter: {} },
  ];
  return (
    <div className="space-y-5" data-testid="b2c-overview">
      <div className="flex items-center gap-3">
        <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Visão Geral" : "Overview"}</h2>
        <button type="button" onClick={reload} data-testid="b2c-overview-refresh"
          className="ml-auto h-8 px-3 rounded-lg border border-[var(--al-border)] text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-[var(--al-row-alt)]">
          <RefreshCw className="w-3.5 h-3.5" /> {isPt ? "Atualizar" : "Refresh"}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((k) => (
          <button key={k.label} type="button"
            data-testid={`b2c-overview-kpi-${k.target}`}
            onClick={() => { seedFilter(k.filter); goTo && goTo(k.target); }}
            className="al-card p-4 text-left hover:shadow-lg hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] transition-all cursor-pointer">
            <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">{k.label}</div>
            <div className={`text-[26px] font-bold mt-1 tabular-nums ${k.tone}`}>{k.value}</div>
            <div className="text-[10px] text-[var(--al-text-muted)] mt-1 italic">{isPt ? "Clique para ver →" : "Click to view →"}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="al-card p-5">
          <h3 className="text-[14px] font-bold mb-3">{isPt ? "Menções por fonte" : "Mentions by source"}</h3>
          {(data.by_source || []).length === 0
            ? <p className="text-[12px] text-[var(--al-text-muted)] italic">{isPt ? "Sem dados ainda. Faça uma busca em Reddit & Fóruns." : "No data yet. Run a search in Reddit & Forums."}</p>
            : <BarList items={data.by_source.map((s) => ({ label: s.source, value: s.count }))} />}
        </div>
        <div className="al-card p-5">
          <h3 className="text-[14px] font-bold mb-3">{isPt ? "Menções por intenção" : "Mentions by intent"}</h3>
          {(data.by_intent || []).length === 0
            ? <p className="text-[12px] text-[var(--al-text-muted)] italic">{isPt ? "Sem dados ainda." : "No data yet."}</p>
            : <BarList items={data.by_intent.map((s) => ({ label: s.category, value: s.count }))} />}
        </div>
      </div>
    </div>
  );
}

function BarList({ items }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.label} className="text-[12.5px]">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold capitalize">{i.label}</span>
            <span className="tabular-nums text-[var(--al-text-muted)]">{i.value}</span>
          </div>
          <div className="h-1.5 rounded bg-[var(--al-row-alt)] overflow-hidden">
            <div className="h-full bg-[#8B5CF6]" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---- Reddit / Mentions search shared form ----
function SearchPanel({ isPt, defaultSources, onResults, testidPrefix }) {
  // Iter51 — start empty; show example as placeholder so the field clears on
  // click. Previously it was pre-filled with "looking for CRM" which the user
  // had to manually delete every time.
  const [keywords, setKeywords] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoRunKey, setAutoRunKey] = useState(0);
  const sources = defaultSources;

  // Consume keyword seeded by the header quick-search bar. The seed comes
  // from EITHER localStorage (first mount) OR a CustomEvent (when the panel
  // is already mounted and the user re-submits the header bar). Iter86 —
  // also auto-trigger the search so the user doesn't have to click Search
  // a second time after using the header bar.
  useEffect(() => {
    try {
      const seeded = window.localStorage.getItem("alp.b2c.quick_kw");
      if (seeded) {
        setKeywords(seeded);
        window.localStorage.removeItem("alp.b2c.quick_kw");
        setAutoRunKey((k) => k + 1);
      }
    } catch (_e) { /* ignore */ }
    const handler = (e) => {
      if (e?.detail?.keywords) {
        setKeywords(e.detail.keywords);
        setAutoRunKey((k) => k + 1);
      }
    };
    window.addEventListener("alp.b2c.quickSearch", handler);
    return () => window.removeEventListener("alp.b2c.quickSearch", handler);
  }, []);

  const run = async () => {
    const list = keywords.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 8);
    if (list.length === 0) {
      toast.error(isPt ? "Digite pelo menos uma palavra-chave" : "Enter at least one keyword");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/b2c-intent/search`,
        { keywords: list, sources, limit_per_source: 12 }, { timeout: 180000 });
      const found = data.preview || [];
      onResults(found);
      toast.success(isPt
        ? `${data.inserted} novas, ${data.already_known || 0} já capturadas, ${data.high_intent} alta intenção`
        : `${data.inserted} new, ${data.already_known || 0} already captured, ${data.high_intent} high intent`);
      if (data.pending_sources > 0) {
        toast.info(isPt
          ? "Reddit ainda carregando em segundo plano — novas menções aparecem no Monitoramento em ~1-2 min"
          : "Reddit still loading in background — new mentions will appear in Monitoring in ~1-2 min");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // Auto-fire the search whenever a seed bumps the autoRunKey. We can't
  // call run() inside the seed effect directly because `run` closure would
  // see the old `keywords` value — wait for React to re-render with the
  // new state, then trigger.
  useEffect(() => {
    if (autoRunKey === 0) return;
    if (!keywords.trim()) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunKey]);

  return (
    <div className="al-card p-4 flex flex-wrap gap-2 items-end" data-testid={`${testidPrefix}-form`}>
      <label className="flex-1 min-w-[260px]">
        <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1">
          {isPt ? "Palavras-chave (separe com vírgula)" : "Keywords (comma-separated)"}
        </div>
        <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
          placeholder={isPt
            ? "ex.: procuro CRM, alguém recomenda CRM, melhor plano de saúde"
            : "e.g.: looking for CRM, anyone recommend CRM, best health plan"}
          data-testid={`${testidPrefix}-kw`}
          className="w-full al-input h-9 text-[12.5px]" />
      </label>
      {keywords && (
        <button type="button" onClick={() => setKeywords("")}
          data-testid={`${testidPrefix}-clear`}
          title={isPt ? "Limpar" : "Clear"}
          className="h-9 w-9 rounded-lg border border-[var(--al-border)] text-[var(--al-text-muted)] hover:bg-[var(--al-row-alt)] inline-flex items-center justify-center">
          <XIcon className="w-3.5 h-3.5" />
        </button>
      )}
      <button type="button" onClick={run} disabled={busy}
        data-testid={`${testidPrefix}-go`}
        className="h-9 px-4 rounded-lg bg-[#8B5CF6] hover:bg-[#7c4be0] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
        {isPt ? "Buscar agora" : "Search now"}
      </button>
    </div>
  );
}

function MentionCard({ m, onReply, isPt }) {
  const tone = m.intent_score >= 70 ? "emerald" : m.intent_score >= 40 ? "amber" : "gray";
  const cls = { emerald: "border-emerald-300 bg-emerald-50/50", amber: "border-amber-300 bg-amber-50/50", gray: "border-[var(--al-border)]" }[tone];
  const sentTone = m.sentiment === "positive" ? "text-emerald-600" : m.sentiment === "negative" ? "text-rose-600" : m.sentiment === "urgent" ? "text-amber-600" : "text-[var(--al-text-muted)]";
  return (
    <div className={`al-card p-4 border ${cls}`} data-testid={`b2c-mention-${m.id}`}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="text-[10px] uppercase font-bold tracking-wide text-[#8B5CF6]">{m.source}</span>
        {m.subreddit && <span className="text-[10px] font-semibold text-[var(--al-text-muted)]">r/{m.subreddit}</span>}
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ml-auto ${tone === "emerald" ? "bg-emerald-100 text-emerald-700" : tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
          Score {m.intent_score}
        </span>
      </div>
      <a href={m.url} target="_blank" rel="noopener noreferrer"
         data-testid={`b2c-mention-link-${m.id}`}
         className="text-[14px] font-bold leading-snug hover:underline block">
        {m.title}
      </a>
      {m.snippet && <p className="text-[12px] text-[var(--al-text-muted)] mt-1 line-clamp-3">{m.snippet}</p>}
      <div className="flex items-center gap-3 mt-2.5 text-[11px] flex-wrap">
        <span className="font-semibold capitalize">{m.intent_category}</span>
        <span className={`font-semibold ${sentTone}`}>· {m.sentiment}</span>
        {m.matched_signals && m.matched_signals.length > 0 && (
          <span className="text-[var(--al-text-muted)]">· "{m.matched_signals[0]}"</span>
        )}
        <button type="button" onClick={() => onReply(m)}
          data-testid={`b2c-mention-reply-${m.id}`}
          className="ml-auto h-7 px-2.5 rounded-md bg-[#8B5CF6] text-white text-[11px] font-bold inline-flex items-center gap-1 hover:bg-[#7c4be0]">
          <Bot className="w-3 h-3" /> {isPt ? "Resposta IA" : "AI reply"}
        </button>
      </div>
    </div>
  );
}

function ReplyModal({ open, mention, isPt, onClose }) {
  const [style, setStyle] = useState("consultive");
  const [brand, setBrand] = useState("");
  const [pitch, setPitch] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  if (!open || !mention) return null;
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/b2c-intent/replies/generate`, {
        mention_id: mention.id, style, own_brand: brand, own_pitch: pitch,
      }, { timeout: 60000 });
      setReply(data.reply.reply_text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(reply);
    toast.success(isPt ? "Copiado" : "Copied");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="al-card p-6 max-w-lg w-full space-y-4" data-testid="b2c-reply-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-[16px] font-bold">{isPt ? "Gerar resposta com IA" : "Generate AI reply"}</h3>
            <p className="text-[11.5px] text-[var(--al-text-muted)] mt-0.5">{(mention.title || "").slice(0, 80)}</p>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--al-text-muted)] hover:text-rose-500 text-xl leading-none">×</button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["consultive","direct","educational"].map((s) => (
            <button key={s} type="button" onClick={() => setStyle(s)}
              className={`py-2 px-2 text-[11.5px] font-bold uppercase rounded-lg border ${style === s ? "bg-[#8B5CF6] text-white border-[#8B5CF6]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#8B5CF6]"}`}>
              {s === "consultive" ? (isPt?"Consultiva":"Consultive") : s === "direct" ? (isPt?"Direta":"Direct") : (isPt?"Educativa":"Educational")}
            </button>
          ))}
        </div>
        <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)}
          placeholder={isPt ? "Sua marca (opcional)" : "Your brand (optional)"}
          className="w-full al-input h-9 text-[12.5px]" />
        <textarea value={pitch} onChange={(e) => setPitch(e.target.value)}
          placeholder={isPt ? "Pitch curto (opcional)" : "Short pitch (optional)"}
          className="w-full al-input text-[12.5px] p-2 h-16 resize-none" />
        <button type="button" onClick={generate} disabled={busy}
          data-testid="b2c-reply-generate"
          className="w-full h-10 rounded-lg bg-[#8B5CF6] hover:bg-[#7c4be0] text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
          {isPt ? "Gerar resposta" : "Generate reply"}
        </button>
        {reply && (
          <div>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)}
              data-testid="b2c-reply-output"
              className="w-full al-input text-[12.5px] p-3 h-44 resize-none" />
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={copy} className="h-8 px-3 rounded-lg bg-[#1B3A8A] text-white text-[11.5px] font-bold">
                {isPt ? "Copiar" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MentionsTab({ isPt }) {
  const [items, setItems] = useState([]);
  const [reply, setReply] = useState({ open: false, mention: null });
  // Iter53 — pick up filter seeded by Overview KPIs (since_today,
  // intent_category, intent_min). We display a dismissible chip and apply
  // the filter client-side because the existing API doesn't expose them.
  const [activeFilter, setActiveFilter] = useState(() => {
    try {
      const raw = window.localStorage.getItem("alp.b2c.overview_filter");
      if (raw) {
        const f = JSON.parse(raw);
        window.localStorage.removeItem("alp.b2c.overview_filter");
        return Object.keys(f).length ? f : null;
      }
    } catch (_e) { /* ignore */ }
    return null;
  });
  const reload = async () => {
    const r = await axios.get(`${API}/b2c-intent/mentions?limit=200`);
    setItems(r.data.items || []);
  };
  useEffect(() => { reload(); }, []);
  const filtered = useMemo(() => {
    if (!activeFilter) return items;
    return items.filter((m) => {
      if (activeFilter.intent_category && m.intent_category !== activeFilter.intent_category) return false;
      if (activeFilter.intent_min && (m.intent_score || 0) < activeFilter.intent_min) return false;
      if (activeFilter.since_today) {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const mt = m.created_at ? new Date(m.created_at) : null;
        if (!mt || mt < today) return false;
      }
      return true;
    });
  }, [items, activeFilter]);
  return (
    <div className="space-y-4" data-testid="b2c-mentions">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Monitoramento de menções" : "Mention monitoring"}</h2>
      {activeFilter && (
        <div data-testid="b2c-mentions-active-filter"
             className="rounded-lg border-l-4 border-[#8B5CF6] bg-purple-50 dark:bg-purple-950/20 p-2.5 flex items-center justify-between text-[12px]">
          <span className="text-[var(--al-text)] font-semibold">
            {isPt ? "Filtro ativo:" : "Active filter:"} {activeFilter.intent_category && <code className="ml-1 font-mono text-[11px]">{activeFilter.intent_category}</code>}
            {activeFilter.intent_min && <span className="ml-2">score ≥ {activeFilter.intent_min}</span>}
            {activeFilter.since_today && <span className="ml-2">{isPt ? "hoje" : "today"}</span>}
            <span className="ml-2 text-[var(--al-text-muted)]">({filtered.length}/{items.length})</span>
          </span>
          <button type="button" onClick={() => setActiveFilter(null)}
            data-testid="b2c-mentions-clear-filter"
            className="text-[#8B5CF6] hover:text-[#7C3AED] text-[11px] font-bold">{isPt ? "Limpar filtro" : "Clear filter"}</button>
        </div>
      )}
      <SearchPanel isPt={isPt} defaultSources={["reddit","hackernews"]} onResults={(_) => reload()} testidPrefix="b2c-mentions" />
      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.length === 0
          ? <div className="al-card p-10 text-center text-[var(--al-text-muted)] sm:col-span-2">{isPt ? "Nenhuma menção que corresponda. Rode uma busca acima ou limpe o filtro." : "No matching mentions. Run a search above or clear the filter."}</div>
          : filtered.map((m) => <MentionCard key={m.id} m={m} isPt={isPt} onReply={(mm) => setReply({ open: true, mention: mm })} />)}
      </div>
      <ReplyModal open={reply.open} mention={reply.mention} isPt={isPt} onClose={() => setReply({ open: false, mention: null })} />
    </div>
  );
}

function RedditTab({ isPt }) {
  return <MentionsTab isPt={isPt} />;
}

function AlertsTab({ isPt }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const importRss = async () => {
    if (!url) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/b2c-intent/rss/import`, { url, keyword: "google_alert" });
      toast.success(isPt ? `${data.inserted} novos itens` : `${data.inserted} new items`);
      setUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="b2c-alerts">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Alertas Web Gratuitos" : "Free Web Alerts"}</h2>
      <div className="al-card p-5 space-y-3">
        <h3 className="text-[14px] font-bold">{isPt ? "Importar RSS (Google Alerts, F5Bot, blogs...)" : "Import RSS (Google Alerts, F5Bot, blogs...)"}</h3>
        <p className="text-[12px] text-[var(--al-text-muted)]">
          {isPt
            ? "Crie um alerta em google.com/alerts, escolha 'Enviar para → Feed RSS' e cole a URL aqui. F5Bot.com também envia RSS por palavra-chave."
            : "Create an alert at google.com/alerts, choose 'Deliver to → RSS feed' and paste the URL here. F5Bot.com also offers per-keyword RSS."}
        </p>
        <div className="flex gap-2">
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.google.com/alerts/feeds/..."
            data-testid="b2c-alerts-rss-url"
            className="flex-1 al-input h-9 text-[12.5px]" />
          <button type="button" onClick={importRss} disabled={busy || !url}
            data-testid="b2c-alerts-rss-import"
            className="h-9 px-4 rounded-lg bg-[#8B5CF6] hover:bg-[#7c4be0] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {isPt ? "Importar" : "Import"}
          </button>
        </div>
        <div className="text-[11px] text-[var(--al-text-muted)] pt-2 border-t border-[var(--al-border)] space-y-1">
          <a href="https://www.google.com/alerts" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#8B5CF6] hover:underline">
            <ExternalLink className="w-3 h-3" /> google.com/alerts
          </a>
          <br />
          <a href="https://f5bot.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#8B5CF6] hover:underline">
            <ExternalLink className="w-3 h-3" /> f5bot.com
          </a>
        </div>
      </div>
    </div>
  );
}

function LibraryTab({ isPt }) {
  const [data, setData] = useState({ library: {}, weights: {}, classification: {}, noise_filter: [], reply_tone: "" });
  const [adding, setAdding] = useState({}); // { categoryId: bool }
  const [draft, setDraft] = useState({}); // { categoryId: string }
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/b2c-intent/keyword-library`);
    setData(r.data || { library: {} });
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const labels = {
    high_intent:           { pt: "Alta intenção comercial",        en: "High commercial intent",   color: "emerald" },
    pain_problem:          { pt: "Dor / problema",                 en: "Pain / problem",           color: "amber" },
    comparison:            { pt: "Comparação",                     en: "Comparison",               color: "blue" },
    complaint_competitor:  { pt: "Reclamação contra concorrentes", en: "Competitor complaints",    color: "rose" },
    recommendation_request:{ pt: "Pedido de recomendação",         en: "Recommendation request",   color: "purple" },
  };
  const colors = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-100 text-amber-700 border-amber-200",
    blue:    "bg-blue-100 text-blue-700 border-blue-200",
    rose:    "bg-rose-100 text-rose-700 border-rose-200",
    purple:  "bg-purple-100 text-purple-700 border-purple-200",
  };

  const submitAdd = async (cat) => {
    const kw = (draft[cat] || "").trim();
    if (!kw) return;
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/b2c-intent/keyword-library/add`, { category: cat, keyword: kw });
      if (res.skipped) {
        toast.info(isPt ? "Palavra já existe" : "Keyword already exists");
      } else {
        toast.success(isPt ? "Adicionada" : "Added");
      }
      setDraft({ ...draft, [cat]: "" });
      setAdding({ ...adding, [cat]: false });
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const removeKw = async (cat, kw) => {
    if (!window.confirm(isPt ? `Remover "${kw}"?` : `Remove "${kw}"?`)) return;
    try {
      await axios.delete(`${API}/b2c-intent/keyword-library/remove`, { params: { category: cat, keyword: kw } });
      toast.success(isPt ? "Removida" : "Removed");
      await reload();
    } catch (err) {
      // Built-in keywords aren't in the customs collection — backend returns 404.
      if (err?.response?.status === 404) {
        toast.info(isPt ? "Palavra-chave curada — não pode ser removida." : "Curated keyword — cannot be removed.");
      } else {
        toast.error(err.message);
      }
    }
  };

  const lib = data.library || {};
  const weights = data.weights || {};

  return (
    <div className="space-y-5" data-testid="b2c-library">
      <div>
        <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Palavras-chave de Intenção" : "Intent Keywords"}</h2>
        <p className="text-[12.5px] text-[var(--al-text-muted)] max-w-3xl mt-1">
          {isPt
            ? "Biblioteca curada usada pelo Intent Score, ordenada alfabeticamente. Adicione palavras-chave próprias clicando em + em cada categoria — a palavra é aplicada imediatamente nas próximas buscas."
            : "Curated library powering the Intent Score, sorted alphabetically. Add your own keywords by clicking + on each category — added words apply immediately to future searches."}
        </p>
      </div>

      {/* === Filtro de Intenção Avançado · Configuration card === */}
      <div data-testid="b2c-intent-filter-config"
        className="al-card p-5 border-l-4 border-l-[#8B5CF6] bg-gradient-to-br from-[#8B5CF6]/5 to-transparent">
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-[#8B5CF6]" />
          <h3 className="text-[14px] font-bold">{isPt ? "Filtro de Intenção Avançado" : "Advanced Intent Filter"}</h3>
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-[#8B5CF6]/15 text-[#8B5CF6]">{isPt ? "Configuração" : "Configuration"}</span>
        </div>
        <p className="text-[12.5px] text-[var(--al-text-muted)] mb-4">
          {isPt
            ? "Especialista em Social Listening / Growth Hacking B2C. Critérios aplicados em cada menção do Reddit, fóruns e Web Alerts:"
            : "Social Listening / Growth Hacking B2C specialist. Rules applied to each Reddit, forum and Web Alert mention:"}
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.entries(weights).map(([cat, w]) => {
            const lbl = labels[cat] || { pt: cat, en: cat, color: "purple" };
            return (
              <div key={cat} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--al-border)]">
                <div className={`text-[11px] font-bold px-2 py-1 rounded border ${colors[lbl.color]} min-w-[50px] text-center`}>
                  {isPt ? `Peso ${w}` : `Weight ${w}`}
                </div>
                <div className="text-[12.5px] font-semibold flex-1">{isPt ? lbl.pt : lbl.en}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          {Object.entries(data.classification || {}).map(([code, info]) => {
            const tone = code === "HOT" ? "rose" : code === "WARM" ? "amber" : "blue";
            return (
              <div key={code} data-testid={`b2c-intent-class-${code.toLowerCase()}`}
                className={`p-3 rounded-lg border-2 ${tone === "rose" ? "border-rose-300 bg-rose-50/40" : tone === "amber" ? "border-amber-300 bg-amber-50/40" : "border-blue-300 bg-blue-50/40"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[11px] font-black tracking-wider ${tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-blue-700"}`}>[{code}]</span>
                  <span className="text-[10px] tabular-nums text-[var(--al-text-muted)]">≥ {info.min_score}</span>
                </div>
                <div className="text-[11.5px] text-[var(--al-text)]">{isPt ? info.rule : info.rule_en}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-[var(--al-border)]">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--al-text-muted)] mb-1.5">{isPt ? "Filtro de ruído (descartar)" : "Noise filter (drop)"}</div>
            <ul className="text-[11.5px] space-y-1 list-disc ml-4 text-[var(--al-text)]">
              {(data.noise_filter || []).map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40">
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1.5">{isPt ? "Tom da resposta sugerida" : "Suggested reply tone"}</div>
            <p className="text-[11.5px] text-[var(--al-text)] leading-relaxed">{data.reply_tone}</p>
          </div>
        </div>
      </div>

      {/* === HOT Alerts (Telegram + Twilio) === */}
      <HotAlertsCard isPt={isPt} />

      {/* === Categories === */}
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(lib).map(([cat, list]) => {
          const lbl = labels[cat] || { pt: cat, en: cat, color: "purple" };
          const sorted = [...(list || [])].sort((a, b) => a.localeCompare(b));
          return (
            <div key={cat} className="al-card p-5" data-testid={`b2c-intent-cat-${cat}`}>
              <div className="flex items-center justify-between mb-3 gap-2">
                <h3 className="text-[14px] font-bold flex-1">{(labels[cat] || {})[isPt ? "pt" : "en"] || cat}</h3>
                {weights[cat] != null && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${colors[lbl.color]}`}>{isPt ? `Peso ${weights[cat]}` : `W ${weights[cat]}`}</span>
                )}
                <button type="button" onClick={() => setAdding({ ...adding, [cat]: !adding[cat] })}
                  data-testid={`b2c-intent-cat-${cat}-add-toggle`}
                  className="p-1.5 rounded-md border border-[var(--al-border)] hover:bg-[#8B5CF6]/10 hover:text-[#8B5CF6] hover:border-[#8B5CF6]/40 transition-colors">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              {adding[cat] && (
                <div className="flex gap-2 mb-3">
                  <input
                    autoFocus
                    value={draft[cat] || ""}
                    onChange={(e) => setDraft({ ...draft, [cat]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") submitAdd(cat); if (e.key === "Escape") setAdding({ ...adding, [cat]: false }); }}
                    placeholder={isPt ? "Nova palavra-chave…" : "New keyword…"}
                    data-testid={`b2c-intent-cat-${cat}-input`}
                    className="flex-1 al-input h-8 text-[12px]" />
                  <button type="button" onClick={() => submitAdd(cat)} disabled={busy || !(draft[cat] || "").trim()}
                    data-testid={`b2c-intent-cat-${cat}-submit`}
                    className="h-8 px-3 rounded-md bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[11px] font-bold disabled:opacity-50">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (isPt ? "Adicionar" : "Add")}
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {sorted.length === 0 && (
                  <span className="text-[11px] text-[var(--al-text-muted)] italic">{isPt ? "Sem palavras nesta categoria." : "No keywords in this category."}</span>
                )}
                {sorted.map((kw) => (
                  <span key={kw}
                    className={`group text-[11px] px-2 py-1 rounded border font-semibold inline-flex items-center gap-1 ${colors[lbl.color] || colors.purple}`}>
                    {kw}
                    <button type="button" onClick={() => removeKw(cat, kw)}
                      title={isPt ? "Remover (apenas palavras adicionadas)" : "Remove (user-added only)"}
                      className="opacity-0 group-hover:opacity-100 hover:text-rose-600 transition-opacity">
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HotAlertsCard({ isPt }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tg, setTg] = useState({ enabled: false, bot_token: "", chat_id: "" });
  const [tw, setTw] = useState({ enabled: false, account_sid: "", auth_token: "", from_number: "", to_number: "" });
  const [threshold, setThreshold] = useState(85);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/alerts/config`);
      const c = r.data || {};
      setCfg(c);
      setThreshold(c.threshold || 85);
      setTg({ enabled: !!(c.telegram?.enabled), bot_token: "", chat_id: c.telegram?.chat_id || "" });
      setTw({ enabled: !!(c.twilio?.enabled), account_sid: "", auth_token: "", from_number: c.twilio?.from_number || "", to_number: c.twilio?.to_number || "" });
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setBusy(true);
    try {
      // Send empty token strings as undefined-ish so backend keeps existing values
      // (backend overwrites whatever is sent — so user must re-enter). Display
      // a small hint above to set expectations.
      await axios.post(`${API}/alerts/config`, {
        threshold: Number(threshold),
        telegram: tg,
        twilio: tw,
      });
      toast.success(isPt ? "Configuração salva" : "Configuration saved");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const test = async (channel) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/alerts/test`, null, { params: { channel } });
      const ok = data?.result?.ok;
      if (ok) toast.success(isPt ? `Teste enviado via ${channel}` : `Test sent via ${channel}`);
      else toast.error(`${channel}: ${data?.result?.error || "failed"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="hot-alerts-card"
      className="al-card p-5 border-l-4 border-l-rose-500 bg-gradient-to-br from-rose-50/50 to-transparent dark:from-rose-900/10">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-rose-500" />
        <h3 className="text-[14px] font-bold">{isPt ? "Alertas HOT externos" : "External HOT alerts"}</h3>
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">NEW</span>
      </div>
      <p className="text-[12px] text-[var(--al-text-muted)] mb-4 max-w-3xl">
        {isPt
          ? "Receba menções HOT (intent ≥ threshold) direto no Telegram ou via SMS Twilio para responder em <5 min, mesmo com o dashboard fechado."
          : "Get HOT mentions (intent ≥ threshold) pushed to Telegram or Twilio SMS so you reply in under 5 min — even with the dashboard closed."}
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[11px] font-bold uppercase text-[var(--al-text-muted)]">{isPt ? "Threshold" : "Threshold"}</label>
          <input type="number" min={50} max={100} value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            data-testid="hot-alerts-threshold"
            className="al-input h-9 w-full mt-1 text-[13px]" />
          <div className="text-[10px] text-[var(--al-text-muted)] mt-1">50-100. {isPt ? "Padrão" : "Default"}: 85</div>
        </div>
        <div className="sm:col-span-2 flex items-end">
          <div className="text-[11px] text-[var(--al-text-muted)] leading-snug">
            {isPt
              ? "Dica: tokens são mascarados após salvar. Para trocar, cole o novo valor e clique em Salvar."
              : "Hint: tokens are masked after save. To rotate, paste the new value and click Save."}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Telegram */}
        <div className="p-4 rounded-lg border border-[var(--al-border)] bg-white dark:bg-slate-900/40">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-[#0088cc]" />
              <span className="text-[13px] font-bold">Telegram</span>
            </div>
            <label className="inline-flex items-center gap-2 text-[11px]">
              <input type="checkbox" checked={tg.enabled}
                onChange={(e) => setTg({ ...tg, enabled: e.target.checked })}
                data-testid="hot-alerts-telegram-enabled" />
              {isPt ? "Ativo" : "Enabled"}
            </label>
          </div>
          <input value={tg.bot_token} onChange={(e) => setTg({ ...tg, bot_token: e.target.value })}
            placeholder={cfg?.telegram?.bot_token_masked ? `••• ${cfg.telegram.bot_token_masked} (paste new to rotate)` : "Bot token (BotFather)"}
            data-testid="hot-alerts-telegram-token"
            className="al-input h-9 w-full mb-2 text-[12px]" />
          <input value={tg.chat_id} onChange={(e) => setTg({ ...tg, chat_id: e.target.value })}
            placeholder="Chat ID (numeric)"
            data-testid="hot-alerts-telegram-chat"
            className="al-input h-9 w-full text-[12px]" />
          <button type="button" onClick={() => test("telegram")} disabled={busy || loading}
            data-testid="hot-alerts-telegram-test"
            className="mt-2 text-[11px] font-bold text-[#0088cc] hover:underline disabled:opacity-40">
            {isPt ? "Enviar teste →" : "Send test →"}
          </button>
        </div>

        {/* Twilio */}
        <div className="p-4 rounded-lg border border-[var(--al-border)] bg-white dark:bg-slate-900/40">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#F22F46]" />
              <span className="text-[13px] font-bold">Twilio SMS</span>
            </div>
            <label className="inline-flex items-center gap-2 text-[11px]">
              <input type="checkbox" checked={tw.enabled}
                onChange={(e) => setTw({ ...tw, enabled: e.target.checked })}
                data-testid="hot-alerts-twilio-enabled" />
              {isPt ? "Ativo" : "Enabled"}
            </label>
          </div>
          <input value={tw.account_sid} onChange={(e) => setTw({ ...tw, account_sid: e.target.value })}
            placeholder={cfg?.twilio?.account_sid_masked ? `••• ${cfg.twilio.account_sid_masked} (paste new to rotate)` : "Account SID"}
            data-testid="hot-alerts-twilio-sid"
            className="al-input h-9 w-full mb-2 text-[12px]" />
          <input value={tw.auth_token} onChange={(e) => setTw({ ...tw, auth_token: e.target.value })}
            placeholder="Auth token"
            data-testid="hot-alerts-twilio-token"
            className="al-input h-9 w-full mb-2 text-[12px]" />
          <div className="grid grid-cols-2 gap-2">
            <input value={tw.from_number} onChange={(e) => setTw({ ...tw, from_number: e.target.value })}
              placeholder="From +1…"
              data-testid="hot-alerts-twilio-from"
              className="al-input h-9 w-full text-[12px]" />
            <input value={tw.to_number} onChange={(e) => setTw({ ...tw, to_number: e.target.value })}
              placeholder="To +1…"
              data-testid="hot-alerts-twilio-to"
              className="al-input h-9 w-full text-[12px]" />
          </div>
          <button type="button" onClick={() => test("twilio")} disabled={busy || loading}
            data-testid="hot-alerts-twilio-test"
            className="mt-2 text-[11px] font-bold text-[#F22F46] hover:underline disabled:opacity-40">
            {isPt ? "Enviar teste →" : "Send test →"}
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={save} disabled={busy || loading}
          data-testid="hot-alerts-save"
          className="h-9 px-4 rounded-md bg-rose-500 hover:bg-rose-600 text-white text-[12px] font-bold disabled:opacity-50 inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
          {isPt ? "Salvar configuração" : "Save configuration"}
        </button>
      </div>
    </div>
  );
}

function OpportunitiesTab({ isPt }) {
  const [items, setItems] = useState([]);
  const [reply, setReply] = useState({ open: false, mention: null });
  // Iter53 — filter pickup from Overview KPIs
  const [activeFilter, setActiveFilter] = useState(() => {
    try {
      const raw = window.localStorage.getItem("alp.b2c.overview_filter");
      if (raw) {
        const f = JSON.parse(raw);
        window.localStorage.removeItem("alp.b2c.overview_filter");
        return Object.keys(f).length ? f : null;
      }
    } catch (_e) { /* ignore */ }
    return null;
  });
  const reload = async () => {
    const r = await axios.get(`${API}/b2c-intent/opportunities?limit=200`);
    setItems(r.data.items || []);
  };
  useEffect(() => { reload(); }, []);
  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/b2c-intent/opportunities/${id}`, { status });
    toast.success(isPt ? "Atualizado" : "Updated");
    reload();
  };
  const filtered = useMemo(() => {
    if (!activeFilter) return items;
    return items.filter((o) => {
      if (activeFilter.intent_min && (o.intent_score || 0) < activeFilter.intent_min) return false;
      if (activeFilter.converted && o.status !== "converted") return false;
      return true;
    });
  }, [items, activeFilter]);
  return (
    <div className="space-y-4" data-testid="b2c-opps">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Oportunidades detectadas" : "Detected opportunities"}</h2>
      {activeFilter && (
        <div data-testid="b2c-opps-active-filter"
             className="rounded-lg border-l-4 border-[#10B981] bg-emerald-50 dark:bg-emerald-950/20 p-2.5 flex items-center justify-between text-[12px]">
          <span className="font-semibold">
            {isPt ? "Filtro ativo:" : "Active filter:"}
            {activeFilter.intent_min && <span className="ml-2">score ≥ {activeFilter.intent_min}</span>}
            {activeFilter.converted && <span className="ml-2">{isPt ? "convertidas" : "converted"}</span>}
            <span className="ml-2 text-[var(--al-text-muted)]">({filtered.length}/{items.length})</span>
          </span>
          <button type="button" onClick={() => setActiveFilter(null)}
            data-testid="b2c-opps-clear-filter"
            className="text-emerald-600 hover:text-emerald-800 text-[11px] font-bold">{isPt ? "Limpar" : "Clear"}</button>
        </div>
      )}
      {filtered.length === 0
        ? <div className="al-card p-10 text-center text-[var(--al-text-muted)]">{isPt ? "Nenhuma oportunidade ≥70 ainda. Faça uma busca em Monitoramento." : "No opps ≥70 yet. Run a search under Monitoring."}</div>
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((o) => (
              <div key={o.id} className="al-card p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase text-[#8B5CF6]">{o.source}</span>
                  <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Score {o.intent_score}</span>
                </div>
                <a href={o.url} target="_blank" rel="noopener noreferrer" className="font-bold text-[13.5px] hover:underline">{o.title}</a>
                <p className="text-[12px] text-[var(--al-text-muted)] mt-1 line-clamp-3">{o.summary}</p>
                <div className="flex items-center gap-1 mt-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${o.status === "new" ? "bg-blue-100 text-blue-700" : o.status === "converted" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{o.status}</span>
                  <button type="button" onClick={() => updateStatus(o.id, "converted")} title={isPt ? "Convertida" : "Converted"} className="ml-auto p-1.5 rounded hover:bg-emerald-50 text-emerald-600"><ThumbsUp className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => updateStatus(o.id, "discarded")} title={isPt ? "Descartar" : "Discard"} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><ThumbsDown className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      <ReplyModal open={reply.open} mention={reply.mention} isPt={isPt} onClose={() => setReply({ open: false, mention: null })} />
    </div>
  );
}

function SentimentTab({ isPt }) {
  const [items, setItems] = useState([]);
  // Iter53 — filter pickup
  const [activeFilter, setActiveFilter] = useState(() => {
    try {
      const raw = window.localStorage.getItem("alp.b2c.overview_filter");
      if (raw) {
        const f = JSON.parse(raw);
        window.localStorage.removeItem("alp.b2c.overview_filter");
        return Object.keys(f).length ? f : null;
      }
    } catch (_e) { /* ignore */ }
    return null;
  });
  useEffect(() => { axios.get(`${API}/b2c-intent/mentions?limit=300`).then((r) => setItems(r.data.items || [])); }, []);
  const filtered = useMemo(() => {
    if (!activeFilter || !activeFilter.sentiment) return items;
    return items.filter((m) => m.sentiment === activeFilter.sentiment);
  }, [items, activeFilter]);
  const counts = useMemo(() => {
    const map = { positive: 0, negative: 0, urgent: 0, neutral: 0 };
    items.forEach((m) => { map[m.sentiment || "neutral"] = (map[m.sentiment || "neutral"] || 0) + 1; });
    return map;
  }, [items]);
  return (
    <div className="space-y-4" data-testid="b2c-sentiment">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Análise de Sentimento" : "Sentiment Analysis"}</h2>
      {activeFilter?.sentiment && (
        <div data-testid="b2c-sentiment-active-filter"
             className={`rounded-lg border-l-4 p-2.5 flex items-center justify-between text-[12px] ${activeFilter.sentiment === "positive" ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20" : activeFilter.sentiment === "negative" ? "border-rose-500 bg-rose-50 dark:bg-rose-950/20" : "border-amber-500 bg-amber-50 dark:bg-amber-950/20"}`}>
          <span className="font-semibold">
            {isPt ? "Filtro ativo:" : "Active filter:"} {activeFilter.sentiment}
            <span className="ml-2 text-[var(--al-text-muted)]">({filtered.length}/{items.length})</span>
          </span>
          <button type="button" onClick={() => setActiveFilter(null)}
            data-testid="b2c-sentiment-clear-filter"
            className="text-current opacity-70 hover:opacity-100 text-[11px] font-bold">{isPt ? "Limpar" : "Clear"}</button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="al-card p-5">
            <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">{k}</div>
            <div className={`text-[28px] font-bold tabular-nums ${k === "positive" ? "text-emerald-600" : k === "negative" ? "text-rose-600" : k === "urgent" ? "text-amber-600" : "text-[var(--al-text)]"}`}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RepliesTab({ isPt }) {
  return (
    <div className="space-y-4" data-testid="b2c-replies">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Respostas Sugeridas por IA" : "AI Suggested Replies"}</h2>
      <div className="al-card p-5 max-w-2xl">
        <p className="text-[12.5px] text-[var(--al-text-muted)]">
          {isPt
            ? "Use o botão 'Resposta IA' em qualquer menção em Monitoramento ou Reddit & Fóruns. Você escolhe entre 3 estilos: Consultiva (diagnóstica), Direta (objetiva), Educativa (passo-a-passo). Toda resposta é revisada por humano antes de postar."
            : "Use the 'AI reply' button on any mention under Monitoring or Reddit & Forums. Pick from 3 styles: Consultive (diagnostic), Direct (concise), Educational (step-by-step). Every reply is human-reviewed before posting."}
        </p>
      </div>
    </div>
  );
}

function SourcesTab({ isPt }) {
  const [list, setList] = useState([]);
  useEffect(() => { axios.get(`${API}/b2c-intent/sources`).then((r) => setList(r.data.sources || [])); }, []);
  return (
    <div className="space-y-4" data-testid="b2c-sources">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Fontes Monitoradas" : "Monitored Sources"}</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((s) => (
          <a
            key={s.key}
            href={s.signup_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`b2c-source-${s.key}`}
            className="al-card p-5 block transition hover:shadow-lg hover:border-emerald-300 hover:-translate-y-0.5 cursor-pointer"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-[14.5px] font-bold inline-flex items-center gap-1.5">
                  {s.name}
                  {s.signup_url && <ExternalLink className="w-3.5 h-3.5 text-[var(--al-text-muted)] opacity-60" />}
                </h3>
                <span className="text-[10.5px] uppercase font-bold tracking-wider text-[var(--al-text-muted)]">
                  {s.cost} {s.type ? `· ${s.type.replace(/_/g, " ")}` : ""}
                </span>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase inline-flex items-center gap-1 ${
                (s.status || "ready").startsWith("ready")
                  ? (s.status === "ready_via_apify" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700")
                  : "bg-rose-100 text-rose-700"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  (s.status || "ready").startsWith("ready")
                    ? (s.status === "ready_via_apify" ? "bg-amber-500" : "bg-emerald-500")
                    : "bg-rose-500"
                }`} />
                {(s.status || "ready").startsWith("ready")
                  ? (s.status === "ready_via_apify"
                      ? (isPt ? "Ativa via Apify" : "Active via Apify")
                      : (isPt ? "Pronta para operação" : "Ready to operate"))
                  : (isPt ? "Com problema" : "Error")}
              </span>
            </div>
            <p className="text-[12px] text-[var(--al-text-muted)] mt-2">{s.method}</p>
            <p className="text-[11.5px] text-[var(--al-text-muted)] mt-1.5">
              <b className="text-[var(--al-text)]">{isPt ? "Limitação" : "Limitation"}:</b> {s.limitations}
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] font-bold text-emerald-700">
              {isPt ? "Configurar" : "Configure"} <ArrowRight className="w-3 h-3" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function SettingsTab({ isPt }) {
  return (
    <div className="space-y-4" data-testid="b2c-settings">
      <h2 className="text-[20px] font-bold tracking-tight">{isPt ? "Configurações" : "Settings"}</h2>
      <div className="al-card p-5 max-w-2xl text-[12.5px] text-[var(--al-text-muted)] space-y-2">
        <p>{isPt
          ? "Esta área monitora apenas dados públicos. Nenhuma informação privada, paywall ou login é acessada. As respostas geradas pela IA precisam ser revisadas por você antes de postar."
          : "This module only monitors public data. No private/paywalled/login content is accessed. AI replies must be human-reviewed before publishing."}</p>
        <p>{isPt ? "Versão: B2C Intent v1 · Sources: Reddit, Hacker News, RSS"
                 : "Version: B2C Intent v1 · Sources: Reddit, Hacker News, RSS"}</p>
      </div>
    </div>
  );
}
